import dayjs, { Dayjs } from 'dayjs';
import { LeadTaskModel, TaskOwner } from '@/typings/models/LeadTask';
import { AssigneeModel } from '@/typings/models/Assignee';
import { LeadModel } from '@/typings/models/Lead';

export function hasPermissionToEditTask(task: LeadTaskModel) {
    return task.can_edit && !task.retired_at
}

// Reads both the new owners[] payload and the legacy scalar owner, so the UI
// works against either backend version during the rollout window.
export function getTaskOwners(task?: LeadTaskModel): TaskOwner[] {
  if (!task) return []
  if (Array.isArray(task.owners)) return task.owners
  return task.owner ? [task.owner] : []
}

export function isLeadOwnedTask(task?: LeadTaskModel) {
  return getTaskOwners(task).some(owner => owner?.type === 'lead')
}

export function getTaskLead(task?: LeadTaskModel): LeadModel | null {
  const leadOwner = getTaskOwners(task).find(owner => owner?.type === 'lead')
  if (leadOwner) return leadOwner.data as LeadModel
  if (task?.related_item?.type === 'lead') return task?.related_item?.data as LeadModel
  // Appointment-connected tasks: the related item is the service request, the
  // lead comes embedded on it (same resolution as the backends' Task::getLead)
  if (task?.related_item?.type === 'serviceRequest') {
    return ((task.related_item.data as any)?.lead as LeadModel) || null
  }
  return null
}

// All users the task is assigned to. Lead-owned tasks fall back to the lead's
// assignee — the server resolves the effective assignee the same way.
export function getTaskAssignees(task?: LeadTaskModel): AssigneeModel[] {
  const userOwners = getTaskOwners(task)
    .filter(owner => owner?.type === 'user')
    .map(owner => owner.data as AssigneeModel)
    .filter(Boolean)
  if (userOwners.length > 0) return userOwners

  const fallback = (task as any)?.assignee || getTaskLead(task)?.assignee
  return fallback ? [fallback] : []
}

export function getTaskAssignee(task?: LeadTaskModel): AssigneeModel | null {
  return getTaskAssignees(task)[0] || null
}

export function isTaskSnoozed(task?: LeadTaskModel) {
  if (typeof task?.is_snoozed === 'boolean') return task.is_snoozed
  return Boolean(task?.snooze_until && dayjs(task.snooze_until).isAfter(dayjs()))
}

export const SNOOZE_PRESETS = {
  ONE_DAY: '1d',
  THREE_DAYS: '3d',
  ONE_WEEK: '1w',
} as const;

// The tasks API expects Y-m-d\TH:i:sP; null clears the snooze.
export function buildSnoozeUntil(preset: string | Dayjs | null): string | null {
  if (preset === null) return null
  if (dayjs.isDayjs(preset)) return preset.format('YYYY-MM-DD[T]HH:mm:ssZ')
  const base = dayjs()
  switch (preset) {
    case SNOOZE_PRESETS.ONE_DAY:
      return base.add(1, 'day').format('YYYY-MM-DD[T]HH:mm:ssZ')
    case SNOOZE_PRESETS.THREE_DAYS:
      return base.add(3, 'day').format('YYYY-MM-DD[T]HH:mm:ssZ')
    case SNOOZE_PRESETS.ONE_WEEK:
      return base.add(1, 'week').format('YYYY-MM-DD[T]HH:mm:ssZ')
    default:
      return null
  }
}

// Lead details only return lead-owned tasks; the project uses the /tasks?lead_id list instead,
// which also covers user-owned tasks linked to the lead or one of its appointments.
export function withProjectTasks<T extends Record<string, any>>(lead: T, tasks: LeadTaskModel[], now = dayjs()): T {
  const open = tasks.filter((task) => !task.is_completed && !task.retired_at)
  return {
    ...lead,
    tasks,
    tasks_count: tasks.length,
    completed_tasks_count: tasks.filter((task) => task.is_completed).length,
    overdue_tasks_count: open.filter((task) => task.due_date && dayjs(task.due_date).isBefore(now)).length,
    today_uncompleted_tasks_count: open.filter((task) => task.due_date && !dayjs(task.due_date).isAfter(now, 'day')).length,
    retired_tasks_count: tasks.filter((task) => task.retired_at).length,
  }
}
